import { Button, IconSettings } from '@salesforce/design-system-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setTimeRecords } from '../../actions/time';
import { handleAddTime } from '../../utils/time';
import { Accordion } from '../accordion/Accordion';
import { TimeRecordError } from '../timeRecord/TimeRecordError';
import { TimeRecordItem } from '../timeRecord/TimeRecordItem';
import './Time.scss';

export const Time = () => {
  const dispatch = useDispatch();
  const isComposingEmail = useSelector((state) => state.mail.isComposingEmail);
  const settings = useSelector((state) => state.mail.settings);
  const displaySettings = useSelector((state) => state.mail.settings?.objectsAndDisplayFields);
  const objectsConfiguredForTime = useSelector((state) => state.time?.config?.objectsConfiguredForTime) || [];
  const allSelectedRecords = useSelector((state) => state.mail.selectedRecords);
  const selectedRecords = Object.fromEntries(Object.entries(allSelectedRecords).filter(([key, obj]) => objectsConfiguredForTime.includes(obj.type))) || {};
  const timeRecords = useSelector((state) => state.time.timeRecords) || [];
  const timeConfigFields = useSelector((state) => state.time.config?.fields);
  const timeNamespace = settings?.override?.timeNamespace || 'litify_time';
  const isAllSelectedRecordsNotConfigured = Object.keys(selectedRecords)?.length === 0;
  let displayNamesForRecordObjects = [];
  if (isAllSelectedRecordsNotConfigured) {
    // If all the selected records does not configured for time, then showing all the record object names
    const allRecordObjectNames = Object.values(allSelectedRecords).map(({ type }) => type);
    displayNamesForRecordObjects = displaySettings.filter(({ apiName }) => allRecordObjectNames.includes(apiName)).map(({ pluralLabel }) => pluralLabel);
  } else {
    // If record objects which are not configured for time but any time record with that records are present
    const selectedRecordIdsFromTimeRecords = timeRecords?.map((record) => record[`${timeNamespace}__lit_Related_Record_ID__c`]) || [];
    const recordObjectNames = Object.values(allSelectedRecords).filter(({ Id }) => selectedRecordIdsFromTimeRecords.includes(Id)).map(({ type }) => type);
    const recordObjectNamesNotConfig = recordObjectNames.filter((name) => !objectsConfiguredForTime.includes(name));
    displayNamesForRecordObjects = displaySettings.filter(({ apiName }) => recordObjectNamesNotConfig.includes(apiName)).map(({ pluralLabel }) => pluralLabel);
  }
  const selectedRecordTypes = `${displayNamesForRecordObjects.join(', ')} ${displayNamesForRecordObjects.length > 1 ? 'are' : 'is'}`;
  const isErrorLoadingTimeRecords = useSelector((state) => state.time.isErrorLoadingTimeRecords);
  const isErrorLoadingTimeConfig = useSelector((state) => state.time.isErrorLoadingTimeConfig);
  const timeEnabled = useSelector((state) => state.time.isTimeEnabled);
  // Objects has configured with time and no record object which has time record is not configured with time
  const isTimeObjectConfigured = objectsConfiguredForTime?.length && displayNamesForRecordObjects?.length === 0;
  const user = useSelector((state) => state.auth.user);
  const isEmailSaved = useSelector((state) => state.mail.isEmailSaved);
  const allEmailsSaved = Object.values(isEmailSaved).every((value) => value === true);
  const isSavingToTimeline = useSelector((state) => state.mail.isSavingToTimeline);
  const activityTimelineSelected = allEmailsSaved || isSavingToTimeline;
  const timeTrackingMode = settings?.timeSettings?.timeTrackingMode;
  const [isExpanded, setIsExpanded] = useState(true);
  const [timeConfigObjectMismatch, setTimeConfigObjectMismatch] = useState(false);
  const [singleTimeEntry, setSingleTimeEntry] = useState('entry has');
  const [isTimeError, setIsTimeError] = useState(false);
  const [activeErrorMessage, setActiveErrorMessage] = useState(0);
  const timeRecordMessage = timeTrackingMode === 'timeReminder' && !activityTimelineSelected ? 'Save email activity to add time entry.' : 'No Time logged';

  useEffect(() => {
    if (!isTimeObjectConfigured && timeRecords.length) {
      setSingleTimeEntry(timeRecords.length === 1 ? 'entry has' : 'entries have');
      dispatch(setTimeRecords([]));
      setTimeConfigObjectMismatch(true);
    }
    setIsTimeError(!!timeRecords.find((timeRecord) => timeRecord.errorMessage));
  }, [timeRecords]);

  const addTimeSpan = (
    <span className='time-addTimeSpan'
      onClick={() => handleAddTime(isErrorLoadingTimeConfig, dispatch, false, !activityTimelineSelected, selectedRecords, timeConfigFields) }>
      Add Time
    </span>);

  const noTimeRecords = !isErrorLoadingTimeRecords && (
    <div className='time-noRecords'>
      <span className='time-noRecords-span'>{timeRecordMessage}</span>
    </div>
  );

  // function to get task code from time config
  const getTaskCode = (record) => {
    const taskCode = timeConfigFields
        .find(({ name }) => name === `${timeNamespace}__lit_Task_Code__c`)
        ?.value.find(({ Id }) => Id === record[`${timeNamespace}__lit_Task_Code__c`])
        ?.Name;
    return taskCode;
  };

  return (
    timeConfigObjectMismatch ?
    <div className='time-config-mismatch-toast'>
      {`Time ${singleTimeEntry} been cleared. ${selectedRecordTypes} not configured for time records.`}
      <IconSettings iconPath="/assets">
        <Button variant='icon' className='time-config-mismatch-toast-close-icon' iconName='close' iconCategory='utility' onClick={() => setTimeConfigObjectMismatch(false)}/>
      </IconSettings>
    </div> :
    <div id="time-component" className={timeEnabled && isTimeObjectConfigured ? '' : 'hide'}>
      <Accordion title='Time' link={addTimeSpan} status={isExpanded} setStatus={setIsExpanded} />
      { isTimeError &&
        <div className="time-errorBanner">
          <p>At least one Time Entry did not save, resolve issue to save</p>
        </div>
      }
      { isExpanded && isErrorLoadingTimeRecords && <TimeRecordError/> }
      {
        isExpanded ?
          timeRecords.length === 0 ?
            noTimeRecords :
            timeRecords.map((record, index) => {
              const recordId = record[`${timeNamespace}__lit_Related_Record_ID__c`];
              const selectedRecord = selectedRecords[recordId];
              return (
                <TimeRecordItem
                  key={record.Id}
                  entryDate={record[`${timeNamespace}__lit_Date__c`]}
                  assignedTo={record[`${timeNamespace}__lit_User__c`]}
                  taskCode={getTaskCode(record)}
                  relatedTo={selectedRecord}
                  popoutLink={`${user.salesforceInstanceUrl}/lightning/r/${timeNamespace}__lit_Time_Entry__c/${record.Id}/view`}
                  loggedTime={record[`${timeNamespace}__lit_Duration__c`]}
                  isSaved={record.saved}
                  isPresaved={isComposingEmail && record.saved}
                  index={index}
                  isError={!!record.error}
                  timeRecord={record}
                  activeErrorMessage={activeErrorMessage}
                  setActiveErrorMessage={setActiveErrorMessage}
                />
              );
            },
            ) :
          null
      }
    </div>
  );
};

